import { useMemo, useState } from 'react';
import { Modal, Input, Button, Checkbox, Empty, Dropdown, message as antdMessage } from 'antd';
import {
  WhatsAppOutlined, MessageOutlined, MailOutlined,
  SearchOutlined, SwapOutlined, RightOutlined,
  FilePdfOutlined, LayoutOutlined, LinkOutlined,
} from '@ant-design/icons';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────── */

interface Client {
  name?:        string;
  phoneNumber?: string;
  email?:       string;
}

interface Props {
  open:      boolean;
  onClose:   () => void;
  client:    Client;
}

type Channel = 'whatsapp' | 'sms' | 'email';
type Tab     = 'messages' | 'files' | 'pages';

interface Template {
  id:         string;
  title:      string;
  body:       string;
  sentCount:  number;
  lastShared: string | null;
}

/* ─── Mock templates (replace with API when available) ───────── */

const TEMPLATES: Template[] = [
  { id: 't1', title: 'Follow Up - FitClub', body: 'Hi @clientName, Hope you are doing well, I wanted to check if you had any questions about FitClub, or your fitness goals. Let me know how I can help!', sentCount: 1, lastShared: '24 Jul 2025' },
  { id: 't2', title: 'FITCLUB', body: "Hi @clientName, It's Sunday, and it's time to work hard! You are all hustlers who are ready to give it your best. See you at the club!", sentCount: 0, lastShared: null },
  { id: 't3', title: 'Get Motivated – Start Your Fitness Journey Today!', body: "Hi @clientName Are you ready to take your fitness to the next level? It's time to make today the day you start. Book your first session now!", sentCount: 0, lastShared: null },
  { id: 't4', title: 'Follow Up To Non Member', body: "Hi @clientName Starting at the gym can be a life-changing decision! Here's how to make it part of your routine. Reply to get started.", sentCount: 0, lastShared: null },
  { id: 't5', title: "Ready to Crush Goals Again? Let's Go!", body: 'Hi @clientName, Long time, no reps? 👀💪 Before you give up on your goals — here\'s your friendly nudge to come back stronger.', sentCount: 0, lastShared: null },
  { id: 't6', title: 'Copy of FITCLUB', body: "Hi @clientName, It's Sunday, and it's time to work hard! You are all hustlers who are ready to give it your best.", sentCount: 0, lastShared: null },
];

interface FileItem {
  id:         string;
  name:       string;
  type:       string;
  pages:      number;
  lastShared: string | null;
}

const FILES: FileItem[] = [
  { id: 'f1', name: 'Example - ACME Residences Brochure', type: 'PDF', pages: 3, lastShared: null },
  { id: 'f2', name: '1754369424920_ProductBuilder',        type: 'PDF', pages: 2, lastShared: null },
];

interface PageItem {
  id:          string;
  title:       string;
  sharedCount: number;
  lastShared:  string | null;
}

const PAGES: PageItem[] = [
  { id: 'p1', title: 'Luxury Meets Fitness',          sharedCount: 2231, lastShared: '10 Jul 2026' },
  { id: 'p2', title: 'Long Time , No Reps? 💪👀',      sharedCount: 7,    lastShared: '09 Jun 2026' },
  { id: 'p3', title: 'GROUP CLASSES BY FITCLUB',      sharedCount: 63,   lastShared: '07 Jan 2026' },
  { id: 'p4', title: 'Fitclub Golf Course Road',      sharedCount: 625,  lastShared: '14 May 2025' },
  { id: 'p5', title: "Celebrating Women's Day",       sharedCount: 52,   lastShared: '07 Mar 2025' },
  { id: 'p6', title: 'Join Us for Zumba at Fitclub!', sharedCount: 3,    lastShared: '09 Jan 2025' },
  { id: 'p7', title: 'Hatha Yoga Workshop',           sharedCount: 0,    lastShared: null },
  { id: 'p8', title: "Celebrating Women's Day",       sharedCount: 0,    lastShared: null },
];

/* ─── Helpers ────────────────────────────────────────────────── */

const digits = (p?: string) => (p || '').replace(/[^\d]/g, '');
const fill   = (body: string, name?: string) => body.replace(/@clientName/g, name || 'there');

const THUMB_GRADIENTS = [
  'linear-gradient(135deg, #6366f1, #8b5cf6)',
  'linear-gradient(135deg, #10b981, #06b6d4)',
  'linear-gradient(135deg, #f59e0b, #ef4444)',
  'linear-gradient(135deg, #ec4899, #8b5cf6)',
  'linear-gradient(135deg, #0ea5e9, #6366f1)',
];
const pageThumb = (title: string) => THUMB_GRADIENTS[(title?.length || 0) % THUMB_GRADIENTS.length];

/* ─── Component ──────────────────────────────────────────────── */

const SendQuickResponseModal = ({ open, onClose, client }: Props) => {
  const hasPhone = !!digits(client.phoneNumber);
  const hasEmail = !!client.email;

  const [channel, setChannel]       = useState<Channel>(hasPhone ? 'whatsapp' : hasEmail ? 'email' : 'whatsapp');
  const [tab, setTab]               = useState<Tab>('messages');
  const [search, setSearch]         = useState('');
  const [sortRecent, setSortRecent] = useState(true);
  const [skipReview, setSkipReview] = useState(false);

  // Review/compose screen (shown after selecting an item unless skipped).
  // `chip` is set for file/page shares (renders an attachment bubble + link btn).
  const [review, setReview] = useState<
    { title: string; chip?: { name: string; meta: string }; link?: string; preview?: string } | null
  >(null);
  const [draft, setDraft]   = useState('');

  const channels: { key: Channel; icon: React.ReactNode; label: string; sub: string; disabled: boolean }[] = [
    { key: 'whatsapp', icon: <WhatsAppOutlined />, label: 'WhatsApp', sub: client.phoneNumber || 'No number', disabled: !hasPhone },
    { key: 'sms',      icon: <MessageOutlined />,  label: 'SMS',      sub: client.phoneNumber || 'No number', disabled: !hasPhone },
    { key: 'email',    icon: <MailOutlined />,     label: 'Email',    sub: client.email || 'No email address', disabled: !hasEmail },
  ];

  const templates = useMemo(() => {
    let list = TEMPLATES;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) =>
      sortRecent ? (b.sentCount - a.sentCount) : a.title.localeCompare(b.title),
    );
    return list;
  }, [search, sortRecent]);

  // Actually fire the send via the chosen channel.
  const doSend = (subject: string, text: string) => {
    const phone = digits(client.phoneNumber);
    let url = '';
    if (channel === 'whatsapp') {
      if (!phone) return antdMessage.error('No phone number for WhatsApp');
      url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    } else if (channel === 'sms') {
      if (!phone) return antdMessage.error('No phone number for SMS');
      url = `sms:${phone}?body=${encodeURIComponent(text)}`;
    } else {
      if (!client.email) return antdMessage.error('No email address');
      url = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    }
    window.open(url, '_blank');
    antdMessage.success(`Opening ${channel}…`);
    onClose();
  };

  const handleSelect = (tpl: Template) => {
    const text = fill(tpl.body, client.name);
    if (skipReview) { doSend(tpl.title, text); return; }
    setReview({ title: tpl.title });
    setDraft(text);
  };

  const channelLabel = channel === 'whatsapp' ? 'WHATSAPP' : channel === 'sms' ? 'SMS' : 'EMAIL';

  const files = useMemo(() => {
    if (!search.trim()) return FILES;
    const q = search.toLowerCase();
    return FILES.filter(f => f.name.toLowerCase().includes(q));
  }, [search]);

  const handleSelectFile = (f: FileItem) => {
    const link = `https://files.fitclub.app/${f.id}`;
    const text = `Hi ${client.name || 'there'},\n\nHere's a link to view the latest ${f.name}:\n${link}`;
    if (skipReview) { doSend(f.name, text); return; }
    setReview({
      title: f.name,
      chip: { name: f.name, meta: `${f.type} ${f.pages} page${f.pages > 1 ? 's' : ''}` },
      link,
      preview: pageThumb(f.name),
    });
    setDraft(text);
  };

  const pages = useMemo(() => {
    let list = PAGES;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.title.toLowerCase().includes(q));
    }
    return sortRecent ? [...list].sort((a, b) => b.sharedCount - a.sharedCount) : list;
  }, [search, sortRecent]);

  const handleSelectPage = (p: PageItem) => {
    antdMessage.success(`"${p.title}" selected to send via ${channel}`);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1080}
      centered
      destroyOnHidden
      className="sqr-modal"
      title={null}
    >
      <div className="sqr-body">

        {/* ── Left: recipient + channels ── */}
        <aside className="sqr-left">
          <div className="sqr-to">Send Quick Response to</div>
          <h2 className="sqr-name">{client.name || '—'}</h2>
          <div className="sqr-name-sub">({client.name || '—'})</div>

          <div className="sqr-via">Sending via:</div>
          <div className="sqr-channels">
            {channels.map(c => (
              <button
                key={c.key}
                type="button"
                disabled={c.disabled}
                className={`sqr-channel ${channel === c.key ? 'active' : ''} ${c.disabled ? 'disabled' : ''}`}
                onClick={() => !c.disabled && setChannel(c.key)}
              >
                <span className="sqr-ch-icon">{c.icon}</span>
                <span className="sqr-ch-text">
                  <span className="sqr-ch-label">{c.label}</span>
                  <span className="sqr-ch-sub">{c.sub}</span>
                </span>
                <span className={`sqr-radio ${channel === c.key ? 'on' : ''}`} />
              </button>
            ))}
          </div>

          <label className="sqr-skip">
            <Checkbox checked={skipReview} onChange={e => setSkipReview(e.target.checked)} />
            <span>Skip review screen</span>
          </label>
        </aside>

        {/* ── Right: templates ── */}
        <section className="sqr-right">
          {review ? (
            <div className={`sqr-review ${review.preview ? 'has-preview' : ''}`}>
              <div className="sqr-review-main">
              <button type="button" className="sqr-back" onClick={() => setReview(null)}>
                ‹ BACK
              </button>
              {review.chip ? (
                <div className="sqr-review-chip">
                  <span className="sqr-chip-icon"><FilePdfOutlined /></span>
                  <div>
                    <div className="sqr-chip-name">{review.chip.name}</div>
                    <div className="sqr-chip-meta">{review.chip.meta}</div>
                  </div>
                </div>
              ) : (
                <div className="sqr-review-title">{review.title}</div>
              )}
              <Input.TextArea
                className="sqr-review-body"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                autoSize={{ minRows: 12, maxRows: 20 }}
              />
              <div className="sqr-send-row">
                {review.link && (
                  <Dropdown
                    trigger={['click']}
                    placement="top"
                    menu={{ items: [
                      {
                        key: 'with',
                        label: 'Copy link with message',
                        onClick: () => { navigator.clipboard?.writeText(draft); antdMessage.success('Link with message copied'); },
                      },
                      {
                        key: 'only',
                        label: 'Copy link only',
                        onClick: () => { navigator.clipboard?.writeText(review.link!); antdMessage.success('Link copied'); },
                      },
                    ] }}
                  >
                    <button type="button" className="sqr-link-btn" title="Copy link">
                      <LinkOutlined />
                    </button>
                  </Dropdown>
                )}
                <Button
                  type="primary"
                  className="sqr-send"
                  icon={channel === 'whatsapp' ? <WhatsAppOutlined /> : channel === 'sms' ? <MessageOutlined /> : <MailOutlined />}
                  onClick={() => doSend(review.title, draft)}
                >
                  SEND VIA {channelLabel}
                </Button>
              </div>
              </div>

              {review.preview && (
                <aside className="sqr-review-preview">
                  <div className="sqr-preview-head">
                    <div className="sqr-preview-title">{review.title}</div>
                    <div className="sqr-preview-sub">For {client.name || '—'}</div>
                  </div>
                  <div className="sqr-preview-cover" style={{ background: review.preview }}>
                    <FilePdfOutlined />
                    <span className="sqr-preview-label">{review.title}</span>
                  </div>
                </aside>
              )}
            </div>
          ) : (
          <>
          <div className="sqr-tabs">
            {([['messages', 'Messages'], ['files', 'Files'], ['pages', 'Pages']] as [Tab, string][]).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`sqr-tab ${tab === k ? 'active' : ''}`}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'messages' && (
            <>
              <Input
                allowClear
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search messages"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="sqr-search"
              />

              <button type="button" className="sqr-sort" onClick={() => setSortRecent(s => !s)}>
                SORT BY: {sortRecent ? 'RECENTLY USED' : 'NAME (A–Z)'} <SwapOutlined />
              </button>

              <div className="sqr-list">
                {templates.length === 0 && <Empty description="No messages found" />}
                {templates.map(t => (
                  <div key={t.id} className="sqr-item">
                    <div className="sqr-item-main">
                      <div className="sqr-item-title">{t.title}</div>
                      <div className="sqr-item-body">{fill(t.body, client.name)}</div>
                      <div className={`sqr-item-meta ${t.lastShared ? 'shared' : ''}`}>
                        {t.lastShared
                          ? `Sent ${t.sentCount} time${t.sentCount > 1 ? 's' : ''} · Last Shared ${t.lastShared}`
                          : 'Not shared yet'}
                      </div>
                    </div>
                    <Button type="primary" className="sqr-select" onClick={() => handleSelect(t)}>
                      Select <RightOutlined />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'files' && (
            <>
              <Input
                allowClear
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search files"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="sqr-search"
              />

              <button type="button" className="sqr-sort" onClick={() => setSortRecent(s => !s)}>
                SORT BY: {sortRecent ? 'RECENTLY USED' : 'NAME (A–Z)'} <SwapOutlined />
              </button>

              <div className="sqr-list">
                {files.length === 0 && <Empty description="No files found" />}
                {files.map(f => (
                  <div key={f.id} className="sqr-item">
                    <span className="sqr-file-icon"><FilePdfOutlined /></span>
                    <div className="sqr-item-main">
                      <div className="sqr-item-title">{f.name}</div>
                      <div className="sqr-item-body sqr-file-meta">
                        <strong>{f.type}</strong> · {f.pages} page{f.pages > 1 ? 's' : ''}
                      </div>
                      <div className={`sqr-item-meta ${f.lastShared ? 'shared' : ''}`}>
                        {f.lastShared ? `Last Shared ${f.lastShared}` : 'Not shared yet'}
                      </div>
                    </div>
                    <Button type="primary" className="sqr-select" onClick={() => handleSelectFile(f)}>
                      Select <RightOutlined />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
          {tab === 'pages' && (
            <>
              <Input
                allowClear
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search pages"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="sqr-search"
              />

              <button type="button" className="sqr-sort" onClick={() => setSortRecent(s => !s)}>
                SORT BY: {sortRecent ? 'RECENTLY USED' : 'NAME (A–Z)'} <SwapOutlined />
              </button>

              <div className="sqr-list">
                {pages.length === 0 && <Empty description="No pages found" />}
                {pages.map(p => (
                  <div key={p.id} className="sqr-item">
                    <span className="sqr-page-thumb" style={{ background: pageThumb(p.title) }}>
                      <LayoutOutlined />
                    </span>
                    <div className="sqr-item-main">
                      <div className="sqr-item-title">{p.title}</div>
                      <div className={`sqr-item-meta ${p.lastShared ? 'shared' : ''}`}>
                        {p.lastShared
                          ? `Shared ${p.sharedCount} time${p.sharedCount > 1 ? 's' : ''} · Last Shared ${p.lastShared}`
                          : 'Not shared yet'}
                      </div>
                    </div>
                    <Button type="primary" className="sqr-select" onClick={() => handleSelectPage(p)}>
                      Select <RightOutlined />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
          </>
          )}
        </section>
      </div>
    </Modal>
  );
};

export default SendQuickResponseModal;
